import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { GridCell, GridCellProps } from '@progress/kendo-react-grid'

import ConfirmationModal from '../ConfirmationModal/ConfirmationModal'
import { NoteValue } from './types'

interface NotesCommandCellProps<T> {
  edit(data: NoteValue<T>): void
  add(data: NoteValue<T>): Promise<void>
  update(data: NoteValue<T>): Promise<void>
  discard(data: NoteValue<T>): void
  cancel(data: NoteValue<T>): void
  remove(data: NoteValue<T>): Promise<void>
  editField?: string
  CanEdit?: boolean
  CanDelete?: boolean
}

export const NotesCommandCell =
  <T,>(props: NotesCommandCellProps<T>) =>
  (gridProps: GridCellProps): JSX.Element => {
    const { dataItem } = gridProps

    return (
      <GridCell
        render={NoteCommandCell(props)}
        ariaColumnIndex={dataItem.id}
        dataIndex={dataItem.id}
        dataItem={dataItem}
        id={dataItem.id}
        isSelected
      />
    )
  }

const NoteCommandCell = <T,>(props: NotesCommandCellProps<T>) => {
  const { add, update, discard, cancel, edit, remove, CanEdit = true, CanDelete = true } = props
  const [isLoading, setLoading] = useState(false)
  const [confirmationModal, setConfirmationModal] = useState<{
    visible: boolean
    data?: NoteValue<T>
  }>({ visible: false })

  const handleConfirmationModal = async () => {
    setLoading(true)
    if (confirmationModal?.data) {
      await remove(confirmationModal.data)
    }
    setLoading(false)
  }

  const handleConfirmationModalCancel = async () => {
    setConfirmationModal({
      visible: false,
      data: undefined,
    })
  }

  const handleSave = async (dataItem: NoteValue<T>) => {
    setLoading(true)
    if (dataItem?.id) {
      await update(dataItem)
    } else {
      await add(dataItem)
    }
    setLoading(false)
  }

  const handleRemove = async (dataItem: NoteValue<T>) => {
    setLoading(true)
    setConfirmationModal({ visible: true, data: dataItem })
    setLoading(false)
  }

  return (
    _defaultRendering: React.ReactElement<HTMLTableCellElement> | null,
    gridProps: GridCellProps
  ) => {
    const { dataItem } = gridProps

    const editField = props.editField || 'inEdit'
    const inEdit = dataItem?.[editField]
    const isNewItem = !dataItem.id

    return (
      <>
        {confirmationModal.visible && (
          <ConfirmationModal
            contentText='Are you sure you want to delete this Note?'
            yesButtonText='Proceed'
            onYes={handleConfirmationModal}
            onCancel={handleConfirmationModalCancel}
            visible={confirmationModal.visible}
          />
        )}
        {inEdit ? (
          <td className='k-command-cell'>
            <ButtonGroup>
              <Button
                size='sm'
                onClick={() => handleSave(dataItem)}
                disabled={!dataItem.description || isLoading}
              >
                {isNewItem ? 'Add' : 'Update'}
              </Button>
              <Button
                size='sm'
                variant='outline'
                onClick={() => (isNewItem ? discard(dataItem) : cancel(dataItem))}
              >
                {isNewItem ? 'Discard' : 'Cancel'}
              </Button>
            </ButtonGroup>
          </td>
        ) : (
          <td className='k-command-cell'>
            <ButtonGroup>
              <Button size='sm' onClick={() => edit(dataItem)} disabled={!CanEdit}>
                Edit
              </Button>
              <Button
                size='sm'
                variant='destructive'
                onClick={() => handleRemove(dataItem)}
                disabled={isLoading || !CanDelete}
              >
                Delete
              </Button>
            </ButtonGroup>
          </td>
        )}
      </>
    )
  }
}
